// Гейт отправки серверной формы: заявка на зал (/zal) и запрос консультации
// (/coach). Перенесён из hallForm.ts как есть, чтобы второй форме не держать копию.
// Кнопка всегда активна: на отправке гейт либо пропускает, либо называет в строке-подсказке,
// чего не хватает, и подсвечивает поля. Чистые правила — в hallFormGate.ts.
import { hallGateState, missingRequiredFields, type HallGateState } from './hallFormGate'
import { submitHintText } from './submitHint'

// Глобальное имя из data-callback виджета (_captcha.html.erb): авторендер провайдера
// зовёт функцию по имени из window.
declare global {
  interface Window { onCaptchaToken?: (token: string) => void }
}

// Протухание токена у авторендера не приходит событием (token-expired у Яндекса только
// в JS-рендере, у dCaptcha не описано), поэтому поле токена опрашиваем.
const CAPTCHA_POLL_MS = 1000
// Скрипт провайдера заблокирован (адблок, сеть) — виджет не нарисуется никогда. Через
// это время без единого нарисованного элемента перестаём ждать токен: отправка проходит,
// а сервер ответит своей ошибкой капчи.
const CAPTCHA_RENDER_TIMEOUT_MS = 10_000

// Поле формы помечаем aria-invalid, как у браузера. Видимый заместитель скрытого поля
// (группа чипов) и контейнер капчи — не контролы, им aria-invalid не положен: их
// подсвечивает data-invalid (hall.css, gorchakova.css), а причину скринридеру называет aria-describedby
// на строку подсказки. Своего aria-describedby у этих элементов нет, затирать нечего.
const isControl = (el: Element): boolean => el.matches('input, select, textarea')

function mark(el: HTMLElement, hintId: string): void {
  if (isControl(el)) {
    el.setAttribute('aria-invalid', 'true')
    return
  }
  el.setAttribute('data-invalid', 'true')
  if (hintId) el.setAttribute('aria-describedby', hintId)
}

function unmark(el: HTMLElement): void {
  if (isControl(el)) {
    el.removeAttribute('aria-invalid')
    return
  }
  el.removeAttribute('data-invalid')
  el.removeAttribute('aria-describedby')
}

// У группы чипов фокус получает кнопка с tabindex 0 (roving tabindex пикера), иначе первая.
function focusField(el: HTMLElement): void {
  const target = isControl(el) ? el : el.querySelector<HTMLElement>('[tabindex="0"]') || el.querySelector<HTMLElement>('button, input, select, textarea')
  target?.focus()
}

export function initSubmitGate(form: HTMLFormElement, hint: HTMLElement | null): () => void {
  // Без строки-подсказки гейт не ставим: объяснить отказ негде, а молча глотать клик хуже,
  // чем отдать форму серверу — он ответит своими ошибками.
  if (!hint) return () => {}

  // Пузыри браузера заменяет наш перечень: иначе на пустом required submit до нас не дойдёт.
  form.noValidate = true

  const widget = form.querySelector<HTMLElement>('[data-captcha-field]')
  // Отказ капчи с прошлой отправки (сервер). После новой попытки ту же фразу говорит
  // строка под кнопкой — две одинаковые красные строки подряд не нужны.
  const serverCaptchaError = form.querySelector<HTMLElement>('[data-captcha-error]')
  const fieldName = widget?.dataset.captchaField || null
  const token = (): string =>
    (fieldName && form.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`)?.value) || ''

  let stopped = false
  let widgetFailed = false
  // Подсказка и подсветка — только после первой попытки отправки.
  let attempted = false
  let marked: HTMLElement[] = []

  const state = (): HallGateState => hallGateState({
    missing: missingRequiredFields(form),
    captchaRequired: fieldName !== null && !widgetFailed,
    token: token(),
  })

  const render = (current: HallGateState): void => {
    marked.forEach(unmark)
    marked = []
    if (!attempted) return

    if (serverCaptchaError) serverCaptchaError.hidden = true
    const labelsOf = (invalid: boolean): string[] => current.missing.filter((m) => m.invalid === invalid).map((m) => m.label)
    hint.textContent = submitHintText(labelsOf(false), current.captchaPending, labelsOf(true))
    marked = current.missing.flatMap((m) => m.fields)
    if (current.captchaPending && widget) marked.push(widget)
    marked.forEach((el) => mark(el, hint.id))
  }

  const update = (): void => {
    if (!stopped) render(state())
  }

  form.addEventListener('input', update)
  form.addEventListener('change', update)
  // Виджет может записать поле уже после колбэка — пересчёт следующим тиком.
  if (fieldName) window.onCaptchaToken = () => { setTimeout(update, 0) }

  let timer: ReturnType<typeof setInterval> | undefined
  const stop = (): void => {
    stopped = true
    clearInterval(timer)
  }
  if (fieldName) timer = setInterval(() => (form.isConnected ? update() : stop()), CAPTCHA_POLL_MS)

  if (widget) {
    setTimeout(() => {
      const rendered = Array.from(widget.children).some((el) => !el.matches('input[type="hidden"]'))
      if (!rendered) {
        widgetFailed = true
        update()
      }
    }, CAPTCHA_RENDER_TIMEOUT_MS)
  }

  // Слушатель стоит раньше лоадера формы (initHallForm, initConsultForm): неполная форма гасится здесь,
  // лоадер видит defaultPrevented и не включается.
  form.addEventListener('submit', (event) => {
    const current = state()
    if (current.ready) return

    event.preventDefault()
    attempted = true
    render(current)
    // Пустое поле важнее капчи: фокус на него. Осталась одна капча — показываем её,
    // фокус в чужой iframe не поставить.
    if (current.missing.length) focusField(current.missing[0].fields[0])
    else widget?.scrollIntoView({ block: 'center' })
  })

  return stop
}
